import { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery, Box, Container, Toolbar, LinearProgress, Alert } from '@mui/material';
import Drawer from './Drawer';
import Header from './Header';
import Footer from './Footer';
import HorizontalBar from './Drawer/HorizontalBar';
import Breadcrumbs from 'components/@extended/Breadcrumbs';
import useConfig from 'hooks/useConfig';
import { dispatch } from 'store';
import { openDrawer } from 'store/reducers/menu';
import { MenuOrientation } from 'types/config';
import BeginTour from 'components/guideTour/beginTour';
import {
    useGetApiUserInfoWithHeadersQuery,
    useGetApiUserPreferencesParsedQuery,
} from 'store/userOrg/enhancedUserOrgApi';
import { error500Path } from 'routes/paths';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { SerializedError } from '@reduxjs/toolkit';

const checkIsUnauthorized = (error: FetchBaseQueryError | SerializedError | undefined): boolean => {
    if (!error) return false;

    const hasStatus401 = 'status' in error && error.status === 401;
    const hasOriginalStatus401 = 'originalStatus' in error && error.originalStatus === 401;

    return hasStatus401 || hasOriginalStatus401;
};

const MainLayout = () => {
    const theme = useTheme();
    const matchDownXL = useMediaQuery(theme.breakpoints.down('xl'));
    const downLG = useMediaQuery(theme.breakpoints.down('lg'));
    const [skipGePreferences, setSkipGePreferences] = useState(true);
    const navigate = useNavigate();
    const {
        isLoading: userIsLoading,
        isError: userIsError,
        isSuccess: userIsSuccess,
        data: userData,
        error: userError,
    } = useGetApiUserInfoWithHeadersQuery(undefined);
    const { onChangeFirstBuilderStart, firstBuilderStart } = useConfig();

    const {
        data: preferences,
        isSuccess: preferencesIsSuccess,
        isLoading: preferencesIsLoading,
        isError: preferencesIsError,
        error: preferencesError,
    } = useGetApiUserPreferencesParsedQuery(undefined, { skip: skipGePreferences });

    const isUnauthorized = checkIsUnauthorized(userError) || checkIsUnauthorized(preferencesError);

    useEffect(() => {
        if (preferencesIsSuccess) {
            if (preferences?.firstBuilderStart && preferences?.firstBuilderStart !== firstBuilderStart) {
                onChangeFirstBuilderStart(preferences?.firstBuilderStart);
            }
        }
    }, [firstBuilderStart, preferences?.firstBuilderStart]);

    const { container, miniDrawer, menuOrientation } = useConfig();

    const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

    useEffect(() => {
        if (!isUnauthorized && (preferencesIsError || userIsError)) {
            navigate(error500Path(true));
        }
        if (userIsSuccess) {
            setSkipGePreferences(false);
        }
    }, [userIsError, preferencesIsError, userIsSuccess, isUnauthorized, navigate]);

    useEffect(() => {
        if (!miniDrawer) {
            dispatch(openDrawer(!matchDownXL));
        }
    }, [matchDownXL]);

    return userIsLoading || preferencesIsLoading ? (
        <LinearProgress />
    ) : (
        <Box sx={{ display: 'flex', width: '100%' }}>
            <Header />
            {!isHorizontal ? <Drawer /> : <HorizontalBar />}
            <BeginTour />
            <Box component='main' sx={{ width: 'calc(100% - 260px)', flexGrow: 1, p: { xs: 2, sm: 3 } }}>
                <Toolbar sx={{ mt: isHorizontal ? 8 : 'inherit' }} />
                <Container
                    maxWidth={container ? 'xl' : false}
                    sx={{
                        ...(container && { px: { xs: 0, sm: 2 } }),
                        position: 'relative',
                        minHeight: 'calc(100vh - 110px)',
                        display: 'flex',
                        flexDirection: 'column',
                    }}>
                    {userData?.headers?.alertmessage && userData?.headers?.alertstatus ? (
                        <Alert severity={userData?.headers?.alertstatus} sx={{ mb: 2 }}>
                            {userData?.headers?.alertmessage}
                        </Alert>
                    ) : (
                        <></>
                    )}
                    <Breadcrumbs />
                    <Outlet />
                    <Footer />
                </Container>
            </Box>
        </Box>
    );
};

export default MainLayout;
